import { singularityStore } from "@/store/store";
import { TreeNode } from "../../types/tree";
import { breakdanceUndo } from "@/lib/undo/undo";

type AIJobStatus = "pending" | "running" | "completed" | "failed";

export type AIJobType = 
  | 'section_rewrite'           
  | 'section_image_replacement'  
  | 'background_page_generation' 
  | 'background_block_generation'
  | 'sitemap_generation'        
  | 'image_keyword_generation'  
  | 'batch_section_population';

export type AIJobPriority = 'critical' | 'high' | 'normal' | 'low';

export type AIJobRequiredFields = {
  description: string;
  priority: AIJobPriority;
  dontCreateSnapshot?: true;
} & (
  | { dontCreateSnapshot: true; snapshotLabel?: never }
  | { dontCreateSnapshot?: never; snapshotLabel: string }
);

export type AIJobContextSectionRewrite = {
  jobType: 'section_rewrite';
  documentId: number;
  elementId: TreeNode["id"];
} & AIJobRequiredFields;

export type AIJobContextSectionImageReplacement = {
  jobType: 'section_image_replacement';
  documentId: number;
  elementId: TreeNode["id"];
} & AIJobRequiredFields;

export type AIJobContextBackgroundPageGeneration = {
  jobType: 'background_page_generation';
  pageId: number;
  pageTitle: string;
  description: string;
  priority: AIJobPriority;
};

export type AIJobContextBackgroundBlockGeneration = {
  jobType: 'background_block_generation';
  blockId: number;
  blockTitle: string;
  description: string;
  priority: AIJobPriority;
};

export type AIJobContextSitemapGeneration = {
  jobType: 'sitemap_generation';
  businessName: string;
  businessDescription: string;
  description: string;
  priority: AIJobPriority;
};

export type AIJobContextImageKeywordGeneration = {
  jobType: 'image_keyword_generation';
  documentId: number;
  elementId: TreeNode["id"];
} & AIJobRequiredFields;

export type AIJobContextBatchSectionPopulation = {
  jobType: 'batch_section_population';
  documentId: number;
  pageTitle: string;
  sectionIds: TreeNode["id"][];
} & AIJobRequiredFields;

export type AIJobContext = 
  | AIJobContextSectionRewrite
  | AIJobContextSectionImageReplacement
  | AIJobContextBackgroundPageGeneration
  | AIJobContextBackgroundBlockGeneration
  | AIJobContextSitemapGeneration
  | AIJobContextImageKeywordGeneration
  | AIJobContextBatchSectionPopulation;

export type AIJobStateForUI = {
  context: AIJobContext;
  status: AIJobStatus;
  id: number;
};

export type AIJobCallback = (
  abortController: AbortController
) => Promise<unknown>;

type AIJob = {
  id: number;
  status: AIJobStatus;
  callback: AIJobCallback;
  abortController: AbortController;
  context: AIJobContext;
  priority: AIJobPriority;
};

class AIJobController {
  private jobs: AIJob[] = [];
  private pausedJobTypes = new Set<AIJobType>();

  MAX_JOBS = 3;


  async runPendingJobs() {
    if (
      !this.jobs.some(job => job.status === "pending") ||
      this.jobs.length === 0
    ) {
      return;
    }

    const runningJobs = this.jobs.filter(
      job => job.status === "running"
    ).length;
    if (runningJobs >= this.MAX_JOBS) {
      return;
    }

    const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 };
    const pendingJobs = this.jobs
      .filter(job => job.status === "pending" && !this.pausedJobTypes.has(job.context.jobType))
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
      .slice(0, this.MAX_JOBS - runningJobs);

    pendingJobs.forEach(job => {
      this.runJob(job.id);
    });
  }

  addJob(
    callback: AIJobCallback,
    context: AIJobContext
  ) {
    const abortController = new AbortController();
    const jobId = this.jobs.length + 1;

    const newJob: AIJob = {
      id: jobId,
      status: "pending",
      callback,
      abortController,
      context,
      priority: context.priority
    };

    this.jobs.push(newJob);
    this.runPendingJobs();
    this.updateStore();
  }

  async runJob(jobId: number) {
    const jobIndex = this.jobs.findIndex(j => j.id === jobId);
    if (jobIndex === -1) return;

    this.jobs[jobIndex].status = "running";
    this.updateStore();

    const context = this.jobs[jobIndex].context;
    const isBackgroundJob =
      context.jobType === 'background_page_generation' ||
      context.jobType === 'background_block_generation' ||
      context.jobType === 'sitemap_generation';
    const shouldCreateSnapshot = !isBackgroundJob && !('dontCreateSnapshot' in context && context.dontCreateSnapshot);

    try {
      if (shouldCreateSnapshot) {
        breakdanceUndo.startTransaction();
      }

      await this.jobs[jobIndex].callback(this.jobs[jobIndex].abortController);

      if (shouldCreateSnapshot) {
        breakdanceUndo.endTransaction(context.snapshotLabel!);
      }

      this.jobs[jobIndex].status = "completed";
    } catch (error) {
      console.error(`Job ${jobId} failed:`, error);

      if (shouldCreateSnapshot) {
        breakdanceUndo.cancelTransactionIfItIsStillInProgress();
      }

      this.jobs[jobIndex].status = "failed";
    }

    this.runPendingJobs();
    this.updateStore();
  }

  async abortJob(jobId: number) {
    const jobIndex = this.jobs.findIndex(j => j.id === jobId);
    if (jobIndex === -1) return;
    this.jobs[jobIndex].abortController.abort();
    this.jobs[jobIndex].status = "failed";

    this.runPendingJobs();
    this.updateStore();
  }

  /**
   * cancel jobs matching all the specified parameters (AND)
   */
  cancelJobsMatching(filters: {
    documentId?: number;
    elementId?: TreeNode["id"];
    pageId?: number;
    blockId?: number;
    jobType?: AIJobType;
  }) {
    const jobsToCancel = this.jobs.filter(job => 
      job.status === "pending" || job.status === "running"
    ).filter(job => {
      const context = job.context;
      
      if (filters.jobType && context.jobType !== filters.jobType) {
        return false;
      }
      
      if (filters.documentId && 'documentId' in context && context.documentId !== filters.documentId) {
        return false;
      }
      
      if (filters.elementId) {
        if ('elementId' in context && context.elementId !== filters.elementId) {
          return false;
        }
        if ('sectionIds' in context && !context.sectionIds.includes(filters.elementId)) {
          return false;
        }
      }
      
      if (filters.pageId && 'pageId' in context && context.pageId !== filters.pageId) {
        return false;
      }
      
      if (filters.blockId && 'blockId' in context && context.blockId !== filters.blockId) {
        return false;
      }
      
      return true;
    });

    jobsToCancel.forEach(job => this.abortJob(job.id));
  }

  clearAllJobs() {
    this.jobs = [];
    this.updateStore();
  }

  pauseJobType(jobType: AIJobType) {
    this.pausedJobTypes.add(jobType);
    console.log(`Paused job type: ${jobType}`);
  }

  unpauseJobType(jobType: AIJobType) {
    this.pausedJobTypes.delete(jobType);
    console.log(`Unpaused job type: ${jobType}. Running pending jobs...`);
    this.runPendingJobs();
  }

  isJobTypePaused(jobType: AIJobType): boolean {
    return this.pausedJobTypes.has(jobType);
  }

  updateStore() {
    const jobsForUI: AIJobStateForUI[] = this.jobs.map(job => ({
      status: job.status,
      id: job.id,
      context: job.context
    }));

    singularityStore.setAIJobs(jobsForUI);
  }
}

export const aiJobController = new AIJobController();

export function addAiJobAndGetValueReturnedByCallback<T>(
  callback: (abortController: AbortController) => Promise<T>,
  context: AIJobContext
): Promise<T> {
  return new Promise((resolve, reject) => {
    aiJobController.addJob(
      async (abortController) => {
        try {
          const result = await callback(abortController);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      },
      context
    );
  });
}
